import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  $countryStore,
  $recipientCreateStore,
  $recipientMandatoryFieldsStore,
  $recipientUpdateStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils } from '@shared/utils';
import type { ApiResponse } from '@shared/api/models';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { type RecipientResponse, TransferRecipientAccountTypeEnum, TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { TransferRequestTypeEnum } from '@shared/api/rest/transferRequests';
import { AmountTypeEnum } from '@shared/api/rest/payment';
import {
  AccountFormBlock,
  AddressFormBlock,
  CompanyFormBlock,
  FilesFormBlock,
  PaymentFormBlock,
  RecipientSelectBlock,
  TransferConfirmationBlock,
} from '@widgets';
import { Button, type FileWithId, type CustomFile, Spinner } from '@shared/widgets';

export interface IFormDataBusinessOther {
  recipient?: RecipientResponse | null;
  bankCountry?: string;
  currency?: string;
  accountNumber: string;
  accountType?: TransferRecipientAccountTypeEnum;
  bankName: string;
  iban: string;
  bic: string;
  companyName: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  companyCountry: string;
  bankProvince?: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  province?: string;
  amountType: AmountTypeEnum;
  amount?: number;
  beneficiaryAmount?: number;
  paymentReason: string;
  purpose: string;
  fastPayment: boolean;
  files: (FileWithId | CustomFile)[];
}
const DEFAULT_VALUES = {
  bankCountry: undefined,
  currency: undefined,
  accountNumber: '',
  accountType: undefined,
  iban: '',
  bankName: '',
  bic: '',
  companyName: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  companyCountry: '',
  bankProvince: undefined,
  bankAddress: '',
  bankCity: '',
  branchCode: '',
  province: undefined,
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  fastPayment: false,
  files: [] as (FileWithId | CustomFile)[],
} as IFormDataBusinessOther;

interface IProps {
  account: AccountResponse | null;
}

export const BusinessTab = (props: IProps) => {
  const { slug } = useParams<{ slug: 'sepa' }>();

  const countryStore = useUnit($countryStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);

  const [isShowRecipientDetails, setIsShowRecipientDetails] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [createdRecipientData, setCreatedRecipientData] = useState<RecipientResponse | null>(null);
  const [recipientId, setRecipientId] = useState<ApiResponse<RecipientResponse>>();

  const formId = useId();
  const formControl = useForm<IFormDataBusinessOther>({ defaultValues: { ...DEFAULT_VALUES, currency: slug ? 'EUR' : undefined } });
  const recipient = formControl.watch('recipient');
  const amount = formControl.watch('amount');
  const beneficiaryAmount = formControl.watch('beneficiaryAmount');
  const currency = formControl.watch('currency');
  const companyCountry = formControl.watch('companyCountry');
  const bankCountry = formControl.watch('bankCountry');
  const fastPayment = formControl.watch('fastPayment');

  const { t } = useTranslation('payments');

  useEffect(() => {
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (!recipient) return;
    setIsShowRecipientDetails(true);
  }, [recipient]);

  const requestFeeHandler = useCallback(async () => {
    if (amount && (recipient?.bankCountryCode || bankCountry)) {
      await $transferRequestsFeeStore.events.update({
        AccountId: props.account?.id,
        SenderAmount: amount,
        FastPayment: fastPayment,
        BeneficiaryCurrencyCode: recipient?.currency || currency,
        BeneficiaryBankCountryCode: recipient?.bankCountryCode || bankCountry,
        TransferRequestType: TransferRequestTypeEnum.OtherBankTransfer,
      });
    }
  }, [amount, bankCountry, currency, fastPayment, recipient, props.account?.id]);
  useEffect(() => {
    requestFeeHandler();
  }, [props.account?.id, recipient, currency, bankCountry]);

  const countryOptions = useMemo(() => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })), [countryStore]);
  const BankCountryName = countryOptions.find((option) => option.value === bankCountry);
  const ResidenceCountryName = countryOptions.find((option) => option.value === companyCountry);
  useEffect(() => {
    if (BankCountryName && ResidenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.get({
        ResidenceCountryCode: ResidenceCountryName?.value,
        BankCountryCode: BankCountryName?.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Business,
      });
    }
  }, [BankCountryName, ResidenceCountryName, currency]);
  useEffect(() => {
    if (props.account?.id && props.account?.currency && (recipient?.currency || currency) && (bankCountry || recipient?.bankCountryCode)) {
      if (props.account.currency === (recipient?.currency || currency)) {
        $transferRequestsGetFxRateStore.events.setRate(1);
        return;
      }
      $transferRequestsGetFxRateStore.events.update({
        AccountId: props.account?.id,
        FromCurrency: props.account?.currency,
        ToCurrency: recipient?.currency || currency,
        CountryCode: bankCountry || recipient?.bankCountryCode,
      });
    }
  }, [props.account, currency, bankCountry, formControl.formState.isValid, recipient]);

  const onSubmit = async (data: IFormDataBusinessOther) => {
    let recipientTmp = data.recipient;
    if (recipient == null) {
      const request = recipientId === undefined ? await onCreateRecipient(data) : await onUpdateRecipient(data);
      recipientTmp = (request as unknown as { data: RecipientResponse | null }).data;
    }
    if (!recipientTmp || !props.account) return;
    setCreatedRecipientData(recipientTmp);

    if (transferRequestsCreateStore?.data) {
      await $transferRequestsUpdateStore.events.update({
        id: transferRequestsCreateStore.data?.id,
        type: TransferRequestTypeEnum.OtherBankTransfer,
        fromAccountId: props.account.id,
        beneficiaryCountryCode: recipientTmp.countryCode,
        beneficiaryCurrency: recipientTmp.currency,
        recipientId: recipientTmp.id,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        purpose: data.purpose,
        fastPayment: data.fastPayment,
        StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        formControl.setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const newTransferRequest = await $transferRequestsCreateStore.events.create({
      type: TransferRequestTypeEnum.OtherBankTransfer,
      fromAccountId: props.account.id,
      beneficiaryCountryCode: recipientTmp.countryCode,
      beneficiaryCurrency: recipientTmp.currency,
      recipientId: recipientTmp.id,
      senderAmount: data.amount,
      totalAmount: data.amount,
      beneficiaryAmount: data.beneficiaryAmount,
      reasonOfThePayment: data.paymentReason,
      purpose: data.purpose,
      fastPayment: data.fastPayment,
      StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    if (newTransferRequest.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: newTransferRequest.data.id, files: data.files });
      formControl.setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const onCreateRecipient = async (data: IFormDataBusinessOther) => {
    if (recipient != null) return null;

    const response = await $recipientCreateStore.events.create({
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      currency: currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
    setRecipientId(response);
    return response;
  };
  const onUpdateRecipient = async (data: IFormDataBusinessOther) => {
    return await $recipientUpdateStore.events.update({
      id: recipientId?.data?.id,
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      currency: data.currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = (recipient ? recipient.bankCountryCode : bankCountry)?.toUpperCase();
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data, bankCountry, recipient]);
  // const totalAmount = (amount || 0) + (feeStore.data || 0);
  return (
    <FormProvider {...formControl}>
      {formControl.formState.isSubmitting && <Spinner position />}
      <form id={formId} noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
        <div className='bg-white p-6 dark:bg-soft-black'>
          <Controller
            name='recipient'
            control={formControl.control}
            rules={{
              validate: (value) => {
                if (value === undefined) return t('errors.form.required', { ns: 'common' });
                return true;
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <RecipientSelectBlock
                type={TransferRecipientTypeEnum.Business}
                isInternalAccount={false}
                recipient={value}
                onChangeRecipient={(recipient) => {
                  onChange(recipient);
                  formControl.setValue('purpose', '');
                }}
                isShowRecipientDetails={isShowRecipientDetails}
                toggleRecipientDetails={() => setIsShowRecipientDetails((prev) => !prev)}
                error={error?.message}
              />
            )}
          />
          {recipient === null && (
            <div className='mt-6 flex flex-col gap-6'>
              <CompanyFormBlock />
              <AddressFormBlock type='Business' />
              <AccountFormBlock />
            </div>
          )}
        </div>
        <PaymentFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
          onRequestFee={requestFeeHandler}
          currency={currency}
          reasonOptions={reasonOptions}
          account={props.account}
        />
        <FilesFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
          transactionId={transferRequestsCreateStore.data?.id}
        />
      </form>
      <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 rounded-b-xl bg-white p-6 pt-0 dark:bg-soft-black')}>
        <Button
          className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
          color='primary'
          type='submit'
          form={formId}
          disabled={feeStore.data === undefined || feeStore.loading || transferRequestsCreateStore.loading || formControl.formState.isSubmitting}
        >
          {t('form.buttons.transfer')}{' '}
          {beneficiaryAmount && ` ${AmountUtils.transformToAmountWithCurrency(beneficiaryAmount, recipient ? recipient.currency : currency)}`}
        </Button>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          showPaymentModal={showPaymentModal}
          onClosePaymentModal={() => setShowPaymentModal(false)}
          account={props.account!}
          recipient={createdRecipientData}
        />
      )}
    </FormProvider>
  );
};
